import { Inject, Injectable } from '@angular/core';
import { SkyApiDelayedService, SkyApiService } from '@skyeng/libs/shared/api';
import { SkyCryptoService } from '@skyeng/libs/shared/crypto';
import { from, Observable } from 'rxjs';
import { filter, map, switchMap } from 'rxjs/operators';
import { RESOURCE_CONFIG } from '../const';
import {
  ISkyResourceAudio,
  ISkyResourceConfig,
  ISkyResourceImage,
  ISkyResourceVideo,
  SkyResourceWordCard,
} from '../interface';

type ISkyResourceImageWithId = ISkyResourceImage & Required<Pick<ISkyResourceImage, 'id'>>;

@Injectable({ providedIn: 'root' })
export class SkyResourceApi {
  private endpoints = this.config.contentEndpoints;
  private imageEndpoints = this.config.imageEndpoints;

  constructor(
    @Inject(RESOURCE_CONFIG) private config: ISkyResourceConfig,
    private skyApiService: SkyApiService,
    private skyApiDelayedService: SkyApiDelayedService,
    private skyCryptoService: SkyCryptoService,
  ) {}

  loadAudio(audioId: number): Observable<ISkyResourceAudio> {
    return from(this.skyCryptoService.encrypt(audioId)).pipe(
      map(data => encodeURIComponent(data)),
      switchMap(data =>
        this.skyApiService.get<ISkyResourceAudio>(this.endpoints, 'cms/api/audio/' + data, {
          cache: true,
        }),
      ),
    );
  }

  loadImage(imageId: number | string, isRetina = false): Observable<ISkyResourceImage> {
    return this.loadImageResource(imageId, isRetina);
  }

  loadSvgAsString(imageId: number): Observable<string> {
    return this.loadImageResource(imageId).pipe(
      filter(imageLink => !!imageLink?.link),
      switchMap(imageLink =>
        this.skyApiService.get<string>([''], imageLink.link!, {
          cache: true,
          responseType: 'text',
        }),
      ),
    );
  }

  loadVideo(videoId: number): Observable<ISkyResourceVideo | undefined> {
    return this.skyApiDelayedService.delayedRequest('resourceLoadVideo', videoId, videoIds =>
      from(this.skyCryptoService.encrypt(videoIds.join(','))).pipe(
        map(ids => encodeURIComponent(ids)),
        switchMap(ids =>
          this.skyApiService.get<ISkyResourceVideo[]>(this.endpoints, 'cms/api/video', {
            params: { ids },
          }),
        ),
      ),
    );
  }

  loadWordCards(wordCardIds: number[]): Observable<SkyResourceWordCard[]> {
    const ids = wordCardIds.join(',');

    // NOTE: For backward compatibility reason we don't change interface of API method to support working with encrypted ids
    return this.skyApiService.get<SkyResourceWordCard[]>(this.endpoints, '/cms/api/wordcard', {
      cache: true,
      params: { ids },
    });
  }

  uploadFileToS3(url: string, formData: FormData, uploadEventHandlers: {}): Observable<void> {
    return this.skyApiService.post<void>([''], url, {
      formParams: formData,
      responseType: 'text',
      uploadHandlers: uploadEventHandlers,
      longRequest: true,
    });
  }

  private isImagesResolvedById(_images: ISkyResourceImage[]): _images is ISkyResourceImageWithId[] {
    return this.canResolveImageById();
  }

  private canResolveImageById(): boolean {
    return !!this.imageEndpoints;
  }

  private getEndpointsPathForLoadImage(): [string[], string] {
    return this.imageEndpoints ? [this.imageEndpoints, ''] : [this.endpoints, 'cms/api/image'];
  }

  private loadImageResource(imageId: number | string, isRetina = false): Observable<ISkyResourceImage> {
    const [endpoints, path] = this.getEndpointsPathForLoadImage();

    return this.skyApiDelayedService.delayedRequest(
      `resourceLoadImage${isRetina ? 'Retina' : ''}`,
      imageId,
      imageIds =>
        from(this.skyCryptoService.encrypt(imageIds.join(','))).pipe(
          map(ids => encodeURIComponent(ids)),
          switchMap(ids =>
            this.skyApiService.get<ISkyResourceImage[]>(endpoints, path, {
              cache: true,
              params: { ids, isRetina: +isRetina },
            }),
          ),
          map(res => {
            if (this.isImagesResolvedById(res)) {
              return res.reduce(
                (byIdMapping, currentImage) => ({ ...byIdMapping, [currentImage.id]: currentImage }),
                {},
              );
            }

            return res;
          }),
        ),
      { extractById: this.canResolveImageById() },
    );
  }
}
